import { LayoutPqrWeb } from '../../../../layout/pqr_web';
import {
    FormControl,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    Typography,
    Box,
} from '@mui/material';
import LoadingButtonGeneral from '../../../../components/general/buttons/loadingButtonGeneral';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import { usePqrSurvey } from './hook';
import { PqrSurvey } from '../../../../../shared/types/modules/pqr';
import { Question } from './props';
import { FormikProps } from 'formik';
import FieldText from '../../../../components/general/fields/fieldText';
import variables from '../../../../../shared/styles/settings.module.scss';

const renderQuestion = (question: Question, formik: FormikProps<PqrSurvey>) => {
    switch (question.type) {
        case 'scale':
        case 'multiple-choice':
        case 'unique':
            return (
                <FormControl fullWidth>
                    <RadioGroup
                        row={question.type === 'scale'}
                        sx={{
                            justifyContent: 'center',
                            border:
                                formik.errors[question.id] &&
                                formik.touched[question.id]
                                    ? 'solid #d32f2f 1px'
                                    : undefined,
                            borderRadius: '4px',
                        }}
                        value={formik.values[question.id as keyof PqrSurvey]}
                        onChange={(e) =>
                            formik.setFieldValue(
                                question.id as keyof PqrSurvey,
                                e.target.value
                            )
                        }
                    >
                        <Grid container spacing={1} justifyContent="center">
                            {(question.options || []).map((option, index) => (
                                <Grid
                                    item
                                    xs={question.type === 'scale' ? 1 : 6}
                                    key={index}
                                    textAlign={
                                        question.type === 'scale'
                                            ? 'center'
                                            : 'start'
                                    }
                                >
                                    <FormControlLabel
                                        value={option}
                                        control={<Radio />}
                                        labelPlacement={
                                            question.type === 'scale'
                                                ? 'top'
                                                : 'end'
                                        }
                                        label={
                                            <Typography
                                                fontSize="1rem"
                                                fontWeight="bold"
                                                fontFamily="Verdana, Arial, sans-serif"
                                                sx={{
                                                    textAlign:
                                                        question.type ===
                                                        'scale'
                                                            ? 'center'
                                                            : 'left',
                                                }}
                                            >
                                                {option}
                                            </Typography>
                                        }
                                    />
                                    {question?.labels && (
                                        <Typography
                                            fontSize="0.9rem"
                                            sx={{ textAlign: 'center' }}
                                            fontFamily="Verdana, Arial, sans-serif"
                                        >
                                            {question.labels[index]}
                                        </Typography>
                                    )}
                                </Grid>
                            ))}
                            {question.type !== 'scale' &&
                                question.options &&
                                question.options.length % 2 !== 0 && (
                                <Grid item xs={6} />
                            )}
                        </Grid>
                    </RadioGroup>
                    {formik.errors[question.id] &&
                        formik.touched[question.id] && (
                        <Box
                            sx={{
                                color: '#d32f2f',
                                fontSize: '0.75rem',
                                marginLeft: '14px',
                            }}
                        >
                            {formik.errors[question.id]}
                        </Box>
                    )}
                </FormControl>
            );
        case 'textarea':
            return (
                <Grid item xs={12}>
                    <FieldText
                        name={question.id}
                        size="small"
                        required
                        rows={3}
                        onBlur={formik.handleBlur}
                        touched={formik.touched[question.id]}
                        errors={formik.errors[question.id]}
                        value={formik.values[question.id] as string}
                        onChange={formik.handleChange}
                    />
                </Grid>
            );
        default:
            return null;
    }
};

export const PqrSurveyForm = () => {
    const { formik, isLoading, handleBack, questions, canSubmitSurvey } =
        usePqrSurvey();

    return (
        <>
            <LayoutPqrWeb title="Encuesta atención PQR" />
            {canSubmitSurvey && (
                <Grid
                    container
                    spacing={2}
                    px={{ xs: 2, md: 6 }}
                    justifyContent="center"
                >
                    <Grid item xs={12} md={10} lg={8}>
                        <Grid item mt={10}>
                            <Typography
                                fontFamily="Verdana, Arial, sans-serif"
                                variant="h5"
                                gutterBottom
                                sx={{
                                    fontWeight: 'bold',
                                    color: variables.deepPink,
                                }}
                            >
                                Estimado cliente. <br />
                                Para nosotros es muy importante conocer su
                                opinión sobre nuestro servicio.
                            </Typography>
                            <br />
                            <br />
                        </Grid>
                        {questions.map((question) => {
                            if (
                                question.validation &&
                                !question.validation(
                                    formik as FormikProps<PqrSurvey>
                                )
                            )
                                return null;

                            return (
                                <Grid item xs={12} key={question.id}>
                                    <Box
                                        mb={2}
                                        p={2}
                                        borderRadius={2}
                                        sx={{ backgroundColor: '#EDE7F6' }}
                                    >
                                        <Typography
                                            fontFamily="Verdana, Arial, sans-serif"
                                            fontSize="1.2rem"
                                            fontWeight="700"
                                            textAlign="left"
                                            mb={1}
                                        >
                                            {question.text} <span>*</span>
                                        </Typography>
                                        {renderQuestion(
                                            question,
                                            formik as FormikProps<PqrSurvey>
                                        )}
                                    </Box>
                                </Grid>
                            );
                        })}
                        <Grid
                            item
                            xs={12}
                            display="flex"
                            justifyContent="center"
                            gap={2}
                        >
                            <LoadingButtonGeneral
                                loading={isLoading}
                                loadingPosition="end"
                                title="Enviar"
                                endIcon={
                                    <SendIcon
                                        sx={{ color: 'primary.contrastText' }}
                                    />
                                }
                                sx={{ mr: 1 }}
                                onClick={formik.handleSubmit}
                            />
                            <LoadingButtonGeneral
                                title="Volver"
                                startIcon={
                                    <ArrowBackIcon
                                        sx={{ color: 'primary.main' }}
                                    />
                                }
                                variant="outlined"
                                onClick={handleBack}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </>
    );
};
